import { useEffect, useRef } from 'react';

/**
 * Hook to prevent tab/page reloads and improve stability
 * Addresses common causes of page reloading when switching tabs
 */
export function usePageStability() {
  const stabilityRef = useRef(false);

  useEffect(() => {
    if (stabilityRef.current) return; // Already initialized
    stabilityRef.current = true;

    // 1. Prevent aggressive garbage collection
    const keepAliveInterval = setInterval(() => {
      // Keep a small reference to prevent full GC
      window.__pageStability = Date.now();
    }, 30000); // Every 30 seconds

    // 2. Handle visibility changes more gracefully
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - reduce activity but don't let it die
        console.log('[PageStability] Tab hidden - preserving state');
      } else {
        // Page is visible again
        console.log('[PageStability] Tab visible - restoring activity');
        // Force a small DOM update to ensure React is still active
        document.body.style.setProperty('--last-active', Date.now().toString());
      }
    };

    // 3. Prevent browser from killing the tab due to inactivity
    const heartbeat = () => {
      // Very lightweight operation to keep JS engine active
      if (!document.hidden) {
        requestAnimationFrame(heartbeat);
      } else {
        // When hidden, use a slower heartbeat
        setTimeout(heartbeat, 5000);
      }
    };

    // 4. Handle beforeunload more gracefully
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Don't prevent unload, but ensure cleanup
      console.log('[PageStability] Page unloading - cleaning up');
    };

    // 5. Handle memory pressure
    const handleMemoryPressure = () => {
      console.log('[PageStability] Memory pressure detected - cleaning up');
      // Force garbage collection if available
      if (window.gc) {
        window.gc();
      }
    };

    // 6. Prevent React from unmounting due to errors
    const handleError = (event: ErrorEvent) => {
      console.error('[PageStability] JS Error caught:', event.error);
      // Don't let errors crash the app
      event.preventDefault();
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('[PageStability] Unhandled promise rejection:', event.reason);
      // Don't let promise rejections crash the app
      event.preventDefault();
    };

    // Register all event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    // Memory pressure is not universally supported
    if ('memory' in performance) {
      window.addEventListener('memorywarning', handleMemoryPressure);
    }

    // Start the heartbeat
    heartbeat();

    console.log('[PageStability] Stability measures activated');

    // Cleanup function
    return () => {
      clearInterval(keepAliveInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      if ('memory' in performance) {
        window.removeEventListener('memorywarning', handleMemoryPressure);
      }
      console.log('[PageStability] Stability measures deactivated');
    };
  }, []);

  return {
    isStabilityActive: stabilityRef.current
  };
}

// Extend window interface for TypeScript
declare global {
  interface Window {
    __pageStability?: number;
    gc?: () => void;
  }
}
